/* Add sold-out size choices to Nostrum's existing Kbite email popup. */
(() => {
  if (window.nostrumSizeNotifyLoaded) return;
  window.nostrumSizeNotifyLoaded = true;

  let opening = false;
  let activePopupCleanup = null;
  const pause = () => new Promise((resolve) => setTimeout(resolve, 100));

  async function waitForKbite(button) {
    const deadline = Date.now() + 10000;
    while (Date.now() < deadline) {
      if (!button.isConnected) return null;
      const config = window.growConfig;
      if (config?.stockWidget?.isEnabled === false) {
        throw new Error('Kbite stock notifications are disabled.');
      }
      const popupId = config?.stockWidget?.popupSelector;
      const frame = popupId && document.getElementById(popupId);
      const popup = popupId && window.growCampaignPopUps?.[popupId];
      if (
        frame?.tagName === 'IFRAME' && popup?.$data && window.growClient &&
        typeof config.setStockFulFilledVariantOptions === 'function' &&
        typeof config.variantChangedFromModal === 'function'
      ) {
        const popupDocument = frame.contentDocument;
        if (popupDocument?.querySelector('.modal-container .notification-message')) {
          return { config, frame, popup, popupDocument };
        }
      }
      await pause();
    }
    throw new Error('Kbite notification form did not finish loading.');
  }

  function getSizeChoices(button) {
    const picker = button.closest('variant-selects');
    const row = button.closest('[data-nostrum-notify-option-position]');
    const position = row?.dataset.nostrumNotifyOptionPosition;
    const group = [...(picker?.querySelectorAll('[data-nostrum-option-position]') || [])]
      .find((fieldset) => fieldset.dataset.nostrumOptionPosition === position);
    const choices = new Map();
    group?.querySelectorAll('input[data-nostrum-notify-variant-id]').forEach((input) => {
      const id = Number(input.dataset.nostrumNotifyVariantId);
      if (Number.isSafeInteger(id) && id > 0) {
        choices.set(id, { id, label: input.dataset.nostrumNotifySize || input.value, selected: input.checked });
      }
    });
    return { row, choices: [...choices.values()] };
  }

  function addPopupStyles(popupDocument, row) {
    const href = row?.dataset.nostrumNotifyStylesheet;
    if (!href) throw new Error('Notification size styles are missing.');
    let link = popupDocument.querySelector('link[data-nostrum-notify-styles]');
    if (!link) {
      link = popupDocument.createElement('link');
      link.rel = 'stylesheet';
      link.setAttribute('data-nostrum-notify-styles', '');
      popupDocument.head.appendChild(link);
    }
    link.href = href;
    popupDocument.documentElement.classList.add('nostrum-size-notify__popup');
  }

  function mountSizeChoices({ config, popup, popupDocument }, choices) {
    const container = popupDocument.querySelector('.modal-container');
    const message = container?.querySelector('.notification-message');
    if (!message) throw new Error('The Kbite email popup layout has changed.');

    container.querySelector('[data-nostrum-notify-choices]')?.remove();
    const section = popupDocument.createElement('section');
    section.className = 'nostrum-size-notify__choices';
    section.setAttribute('data-nostrum-notify-choices', '');
    section.setAttribute('aria-labelledby', 'nostrum-notify-size-heading');
    const heading = popupDocument.createElement('h3');
    heading.id = 'nostrum-notify-size-heading';
    heading.className = 'nostrum-size-notify__heading';
    heading.textContent = 'Please select your size';
    heading.tabIndex = -1;
    const options = popupDocument.createElement('div');
    options.className = 'nostrum-size-notify__options';
    options.setAttribute('role', 'group');
    options.setAttribute('aria-labelledby', heading.id);
    section.append(heading, options);

    const controls = [...container.querySelectorAll('.email-input, .notify-button')];
    if (!controls.some((control) => control.matches('.email-input')) || !controls.some((control) => control.matches('.notify-button'))) {
      throw new Error('The Kbite email controls could not be found.');
    }
    const originalControlStates = controls.map((control) => ({
      control,
      disabled: control.disabled,
      ariaDisabled: control.getAttribute('aria-disabled'),
    }));
    let selectedId = null;
    function lockEmail(locked) {
      originalControlStates.forEach(({ control, disabled }) => {
        control.disabled = locked || disabled;
        control.setAttribute('aria-disabled', String(control.disabled));
      });
    }
    function guardSubmission(event) {
      const submitClick = event.type === 'click' && event.target instanceof popupDocument.defaultView.Element && event.target.closest('.notify-button');
      if (event.type !== 'submit' && !submitClick) return;
      if (selectedId !== null && Number(config.clickedVariantId) === selectedId && Number(popup.$data.selectedVariant) === selectedId) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      lockEmail(true);
      heading.focus();
    }
    container.addEventListener('click', guardSubmission, true);
    container.addEventListener('submit', guardSubmission, true);

    function selectSize(id) {
      selectedId = id;
      popup.$data.selectedVariant = id;
      config.variantChangedFromModal(id);
      options.querySelectorAll('button').forEach((chip) => {
        chip.setAttribute('aria-pressed', String(Number(chip.dataset.variantId) === id));
      });
      lockEmail(false);
      container.querySelector('.email-input:not(:disabled)')?.focus();
    }

    choices.forEach((choice) => {
      const chip = popupDocument.createElement('button');
      chip.type = 'button';
      chip.className = 'nostrum-size-notify__size';
      chip.dataset.variantId = String(choice.id);
      chip.textContent = choice.label;
      chip.setAttribute('aria-pressed', 'false');
      chip.setAttribute('aria-label', `Notify me when size ${choice.label} is back in stock`);
      chip.addEventListener('click', () => selectSize(choice.id));
      options.appendChild(chip);
    });

    container.insertBefore(section, message);
    popup.$data.selectedVariant = null;
    config.clickedVariantId = null;
    lockEmail(true);
    const nativeSelector = container.querySelector('#variant-container');
    if (nativeSelector) nativeSelector.style.display = 'none';
    return {
      heading,
      dispose() {
        container.removeEventListener('click', guardSubmission, true);
        container.removeEventListener('submit', guardSubmission, true);
        originalControlStates.forEach(({ control, disabled, ariaDisabled }) => {
          control.disabled = disabled;
          if (ariaDisabled === null) control.removeAttribute('aria-disabled');
          else control.setAttribute('aria-disabled', ariaDisabled);
        });
        section.remove();
      },
    };
  }

  document.addEventListener('click', async (event) => {
    const button = event.target instanceof Element
      ? event.target.closest('[data-nostrum-size-notify]')
      : null;
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    if (opening || button.disabled) return;

    const picker = button.closest('variant-selects');
    const status = picker?.querySelector('[data-nostrum-notify-status]');
    if (status) {
      status.textContent = '';
      status.hidden = true;
    }
    opening = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const { row, choices } = getSizeChoices(button);
      if (!choices.length) throw new Error('No sold-out sizes are available for notification.');
      const app = await waitForKbite(button);
      if (!app) return;
      const { config, frame, popup, popupDocument } = app;
      if (typeof window.growClient.ensureCookies === 'function') {
        await window.growClient.ensureCookies();
      }
      if (!button.isConnected) return;
      const preferredId = (choices.find((choice) => choice.selected) || choices[0]).id;
      const productId = Number(button.dataset.productId);
      if (!Number.isSafeInteger(productId) || productId <= 0 || !button.dataset.productHandle) {
        throw new Error('Product notification details are missing.');
      }

      // Prepare Kbite's existing email form. Its stockWidgetClicked handler can
      // immediately subscribe push users, before they have chosen a size.
      config.clickedProductId = productId;
      config.clickedProductHandle = button.dataset.productHandle;
      config.clickedVariantId = preferredId;
      if (typeof config.performTranslation === 'function') config.performTranslation();
      await config.setStockFulFilledVariantOptions();
      if (!button.isConnected) return;
      const appVariantIds = new Set((popup.$data.variants || []).map((variant) => Number(variant.value)));
      const eligibleChoices = choices.filter((choice) => appVariantIds.has(choice.id));
      if (!eligibleChoices.length) throw new Error('Kbite has no matching sold-out sizes.');

      activePopupCleanup?.();
      const originalShowVariantSelector = popup.$data.showVariantSelector;
      popup.$data.showVariantSelector = true;
      if (typeof popup.$nextTick === 'function') await popup.$nextTick();
      addPopupStyles(popupDocument, row);
      const sizeChoices = mountSizeChoices(app, eligibleChoices);
      frame.style.setProperty('display', 'block');

      // Kbite shares this iframe with its ordinary Notify buttons. Restore it
      // after closing so another product or trigger cannot retain these sizes.
      const closeObserver = new MutationObserver(() => {
        if (frame.hidden || frame.style.display === 'none') activePopupCleanup?.();
      });
      activePopupCleanup = () => {
        closeObserver.disconnect();
        sizeChoices.dispose();
        popupDocument.querySelector('[data-nostrum-notify-choices]')?.remove();
        popupDocument.querySelector('link[data-nostrum-notify-styles]')?.remove();
        popupDocument.documentElement.classList.remove('nostrum-size-notify__popup');
        const nativeSelector = popupDocument.querySelector('#variant-container');
        if (nativeSelector) nativeSelector.style.removeProperty('display');
        popup.$data.showVariantSelector = originalShowVariantSelector;
        activePopupCleanup = null;
        if (button.isConnected && frame.style.display === 'none') button.focus();
      };
      closeObserver.observe(frame, { attributes: true, attributeFilter: ['style', 'hidden'] });
      sizeChoices.heading.focus();
    } catch (error) {
      console.error('Nostrum size notification:', error);
      if (status && picker?.isConnected) {
        status.textContent = 'The notification form could not open. Please refresh the page and try again.';
        status.hidden = false;
      }
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      opening = false;
    }
  }, true);
})();
